import olTextBuilder from 'ol/render/canvas/TextBuilder.js';
import {equals} from 'ol/coordinate.js';
import {intersects} from 'ol/extent.js';
import {lineChunk} from 'ol/geom/flat/linechunk.js';
import {matchingChunk} from 'ol/geom/flat/straightchunk.js';
import LineString from 'ol/geom/LineString.js';
import {asColorLike} from 'ol/colorlike.js';
import {getUid} from 'ol/util.js';
import {transform} from 'ol/proj.js';
import {
    defaultFillStyle,
    defaultFont,
    defaultLineCap,
    defaultLineDash,
    defaultLineDashOffset,
    defaultLineJoin,
    defaultLineWidth,
    defaultMiterLimit,
    defaultPadding,
    defaultStrokeStyle,
    defaultTextAlign,
    defaultTextBaseline,
    registerFont
} from 'ol/render/canvas.js';
import CanvasInstruction from 'ol/render/canvas/Instruction.js';

class TextBuilder extends olTextBuilder {}

TextBuilder.prototype.saveTextState_ = function () {
    const strokeState = this.textStrokeState_;
    const textState = this.textState_;
    const fillState = this.textFillState_;
    const strokeKey = this.strokeKey_;

    if (strokeState) {
        if (!(strokeKey in this.strokeStates)) {
            this.strokeStates[strokeKey] = {
                strokeStyle: strokeState.strokeStyle,
                lineCap: strokeState.lineCap,
                lineDashOffset: strokeState.lineDashOffset,
                lineWidth: strokeState.lineWidth,
                lineJoin: strokeState.lineJoin,
                miterLimit: strokeState.miterLimit,
                lineDash: strokeState.lineDash
            };
        }
    }
    const textKey = this.textKey_;
    if (!(textKey in this.textStates)) {
        this.textStates[textKey] = {
            font: textState.font,
            textAlign: textState.textAlign || "center",
            justify: textState.justify
                ? textState.justify
                : (
                    textState.textAlign != 'center'
                        ? textState.textAlign
                        : ''
                ) || '',
            textBaseline: textState.textBaseline || "middle",
            scale: textState.scale
        };
    }
    const fillKey = this.fillKey_;
    if (fillState) {
        if (!(fillKey in this.fillStates)) {
            this.fillStates[fillKey] = {
                fillStyle: fillState.fillStyle
            };
        }
    }
}

TextBuilder.prototype.setTextStyle = function (textStyle, sharedData) {

    let textState,
        fillState,
        strokeState;
    if (!textStyle) {
        this.text_ = '';
    } else {
        const textFillStyle = textStyle.getFill();
        if (!textFillStyle) {
            fillState = null;
            this.textFillState_ = fillState;
        } else {
            fillState = this.textFillState_;
            if (!fillState) {
                fillState = ({});
                this.textFillState_ = fillState;
            }
            fillState.fillStyle = asColorLike(
                textFillStyle.getColor() || defaultFillStyle,
            );
        }

        const textStrokeStyle = textStyle.getStroke();
        if (!textStrokeStyle) {
            strokeState = null;
            this.textStrokeState_ = strokeState;
        } else {
            strokeState = this.textStrokeState_;
            if (!strokeState) {
                strokeState = ({});
                this.textStrokeState_ = strokeState;
            }
            const lineDash = textStrokeStyle.getLineDash();
            const lineDashOffset = textStrokeStyle.getLineDashOffset();
            const lineWidth = textStrokeStyle.getWidth();
            const miterLimit = textStrokeStyle.getMiterLimit();
            strokeState.lineCap = textStrokeStyle.getLineCap() || defaultLineCap;
            strokeState.lineDash = lineDash
                ? lineDash.slice()
                : defaultLineDash;
            strokeState.lineDashOffset = lineDashOffset === undefined
                ? defaultLineDashOffset
                : lineDashOffset;
            strokeState.lineJoin = textStrokeStyle.getLineJoin() || defaultLineJoin;
            strokeState.lineWidth = lineWidth === undefined
                ? defaultLineWidth
                : lineWidth;
            strokeState.miterLimit = miterLimit === undefined
                ? defaultMiterLimit
                : miterLimit;
            strokeState.strokeStyle = asColorLike(
                textStrokeStyle.getColor() || defaultStrokeStyle,
            );
        }

        textState = this.textState_;
        const font = textStyle.getFont() || defaultFont;
        registerFont(font);
        const textScale = textStyle.getScaleArray();
        textState.overflow = textStyle.getOverflow();
        textState.font = font;
        textState.maxAngle = textStyle.getMaxAngle();
        textState.placement = textStyle.getPlacement();
        textState.textAlign = textStyle.getTextAlign();
        textState.repeat = textStyle.getRepeat();
        textState.justify = textStyle.getJustify();
        textState.textBaseline = textStyle.getTextBaseline() || defaultTextBaseline;
        textState.backgroundFill = textStyle.getBackgroundFill();
        textState.backgroundStroke = textStyle.getBackgroundStroke();
        textState.padding = textStyle.getPadding() || defaultPadding;
        textState.scale = textScale === undefined
            ? [1, 1]
            : textScale;

        const textOffsetX = textStyle.getOffsetX();
        const textOffsetY = textStyle.getOffsetY();
        const textRotateWithView = textStyle.getRotateWithView();
        const textKeepUpright = textStyle.getKeepUpright();
        const textRotation = textStyle.getRotation();
        this.text_ = textStyle.getText() || '';
        this.textOffsetX_ = textOffsetX === undefined
            ? 0
            : textOffsetX;
        this.textOffsetY_ = textOffsetY === undefined
            ? 0
            : textOffsetY;
        this.textRotateWithView_ = textRotateWithView === undefined
            ? false
            : textRotateWithView;
        this.textKeepUpright_ = textKeepUpright === undefined
            ? true
            : textKeepUpright;
        this.textRotation_ = textRotation === undefined
            ? 0
            : textRotation;

        this.strokeKey_ = strokeState
            ? (
                typeof strokeState.strokeStyle == 'string'
                    ? strokeState.strokeStyle
                    : getUid(strokeState.strokeStyle)
            ) + strokeState.lineCap + strokeState.lineDashOffset + '|' +
                    strokeState.lineWidth + strokeState.lineJoin + strokeState.miterLimit + '[' +
                    strokeState
                .lineDash
                .join() + ']'
            : '';
        this.textKey_ = textState.font + textState.scale + (textState.textAlign || '?') +
                (textState.repeat || '?') + (textState.justify || '?') + (
            textState.textBaseline || '?'
        );
        this.fillKey_ = fillState && fillState.fillStyle
            ? typeof fillState.fillStyle == 'string'
                ? fillState.fillStyle
                : '|' + getUid(fillState.fillStyle)
            : '';
    }
    this.declutterMode_ = textStyle.getDeclutterMode();
    this.declutterImageWithText_ = sharedData;
}

TextBuilder.prototype.drawText = function (geometry, feature, index) {
    const fillState = this.textFillState_;
    const strokeState = this.textStrokeState_;
    const textState = this.textState_;
    if (this.text_ === '' || !textState || (!fillState && !strokeState)) {
        return;
    }

    const coordinates = this.coordinates;
    let begin = coordinates.length;

    var rotate_for_coords = [];

    const geometryType = geometry.getType();
    let flatCoordinates = null;
    let stride = geometry.getStride();

    var text = this.text_;

    if (!isNaN(text)) {
        text = text.toString();
    }

    var textlength = text.length;
    var resolution = this.resolution;
    var px = 15;
    this
        .textKey_
        .split(' ')
        .forEach(element => {
            if (element.includes('px')) {
                px = parseInt(element);
            }
        });
    var allle = textlength * px;

    function stringDividerstr(str, width, spaceReplacer) {
        spaceReplacer = '\n';
        width = 100;
        if (str.length > width) {
            var p = width;
            while (p > 0 && (str[p] != ' ' && str[p] != '-')) {
                p--;
            }
            if (p > 0) {
                var left;
                if (str.substring(p, p + 1) == '-') {
                    left = str.substring(0, p + 1);
                } else {
                    left = str.substring(0, p);
                }
                var right = str.substring(p + 1);
                return left + spaceReplacer + stringDividerstr(right, width, spaceReplacer);
            }
        }
        return str;
    }

    function polygonsplit(t1) {
        var mycoords = [];
        var counte = parseInt(
            (t1.getArea() / (resolution * resolution)) / (allle * px * px) / 4
        );
        var extent = t1.getExtent();
        var x = extent[2] - extent[0];
        var y = extent[3] - extent[1];
        if (counte > 1) {
            var a,
                b;
            if (x >= y) {
                var diff = (x / y);
                b = Math.ceil((Math.sqrt(counte / diff)));
                a = (b * diff).toFixed(0);
            } else {
                var diff = (y / x);
                a = Math.ceil((Math.sqrt(counte / diff)));
                b = (a * diff).toFixed(0);
            }
            if (a > 0 && b > 0) {
                a = a < 150
                    ? a
                    : 150;
                b = b < 150
                    ? b
                    : 150;
                mycoords = [];
                var adiff = x / (parseInt(a) + 1);
                var bdiff = y / (parseInt(b) + 1);
                for (var ia = 1; ia <= a; ia++) {
                    var erta = (ia * adiff) + extent[0];
                    for (var ib = 1; ib <= b; ib++) {
                        var ertb = (ib * bdiff) + extent[1];
                        if (t1.intersectsCoordinate([erta, ertb])) {
                            mycoords.push(erta, ertb);
                        }
                    }
                }
            } else {
                var dtt = t1.getFlatInteriorPoint();
                mycoords = [
                    dtt[0], dtt[1]
                ];
            }
        } else {
            var dtt = t1.getFlatInteriorPoint();
            mycoords = [
                dtt[0], dtt[1]
            ];
        }
        return mycoords;
    }

    function spliter(coords, allcoords) {
        var tol = 1e-10;
        var pt = coords;
        var c0 = allcoords;
        var ci = [c0[0]];
        var c = [];
        for (var i2 = 0; i2 < c0.length - 1; i2++) {
            // Filter equals points
            if (equals(c0[i2], c0[i2 + 1])) 
                continue;
            
            // Extremity found
            if (equals(pt, c0[i2 + 1])) {
                ci.push(c0[i2 + 1]);
                c.push(new LineString(ci));
                ci = [];
            } else if (!equals(pt, c0[i2])) {
                var d1,
                    d2,
                    split = false;
                if (c0[i2][0] == c0[i2 + 1][0]) {
                    d1 = (c0[i2][1] - pt[1]) / (c0[i2][1] - c0[i2 + 1][1]);
                    split = (c0[i2][0] == pt[0]) && (0 < d1 && d1 <= 1)
                } else if (c0[i2][1] == c0[i2 + 1][1]) {
                    d1 = (c0[i2][0] - pt[0]) / (c0[i2][0] - c0[i2 + 1][0]);
                    split = (c0[i2][1] == pt[1]) && (0 < d1 && d1 <= 1)
                } else {
                    d1 = (c0[i2][0] - pt[0]) / (c0[i2][0] - c0[i2 + 1][0]);
                    d2 = (c0[i2][1] - pt[1]) / (c0[i2][1] - c0[i2 + 1][1]);
                    split = (Math.abs(d1 - d2) <= tol && 0 < d1 && d1 <= 1)
                }
                // pt is inside the segment > split
                if (split) {
                    ci.push(pt);
                    c.push(new LineString(ci));
                    ci = [pt];
                }
            }
            ci.push(c0[i2 + 1]);
        }
        if (ci.length > 1) 
            c.push(new LineString(ci));
        if (c.length) 
            return c;
        else 
            return [];
        }
    
    function coordsplitbycount(t1, count, ert) {
        var t3 = 1 / count;
        var allcoords = [],
            allps = [];
        var last = t1;
        var er = ert;
        for (var co = 1; co <= count; co++) {
            var ds = t3 * co;
            //ds=t3;
            if (last) {
                var dsrtt = spliter(t1.getCoordinateAt(ds), last.getCoordinates());
                // console.log(dsrtt); if (default_draw_layer) {
                // default_draw_layer.getSource().addFeature(new MT.Feature(new
                // MT.geom.Point(t1.getCoordinateAt(ds)))); }

                if (dsrtt.length > 1) {
                    last = dsrtt[1];
                    var mye = dsrtt[0].getFlatCoordinates();
                    er += mye.length;
                    allcoords = allcoords.concat(mye);
                    allps.push(er);
                    if (co == count - 1 && last) {
                        var mye1 = last.getFlatCoordinates();
                        er += mye1.length;
                        allcoords = allcoords.concat(mye1);
                        allps.push(er);
                    }
                } else if (dsrtt.length == 1) {
                    last = dsrtt[0];

                    // if (default_draw_layer) {    default_draw_layer.getSource().addFeature(new
                    // MT.Feature(last)); }
                }
            }
        }
        return [allcoords, allps, er]
    }

    if (geometryType == 'LineString' || geometryType == 'MultiLineString') {
        textState.placement = textState.placement != 'point'
            ? 'line'
            : textState.placement;
    } else {
        if (text) {
            if (!text.includes('\n')) {
                this.text_ = stringDividerstr(text, 16, '');
            }
        }
        textState.placement = 'point';
    }

    var labelCoordinates = feature.get('labelCoordinates') == 'NULL'
        ? false
        : feature.get('labelCoordinates');
    feature.set('labelCoordinates', labelCoordinates, true);

    if (feature.get('labelCoordinates')) {
        textState.placement = 'point';
        if (text) {
            if (!text.includes('\n')) {
                this.text_ = stringDividerstr(text, 16, '');
            }
        }
    }

    if (textState.placement === 'line' && (geometryType == 'LineString' || geometryType == 'MultiLineString' || geometryType == 'Polygon' || geometryType == 'MultiPolygon')) {
        if (!intersects(this.maxExtent, geometry.getExtent())) {
            return;
        }
        let ends;
        flatCoordinates = geometry.getFlatCoordinates();
        if (geometryType == 'LineString') {
            ends = [flatCoordinates.length];

            if (textState.textrepeat_ == true || textState.textrepeat_ == 'true') {
                var featlength = geometry.getLength();
                var counte = (featlength / resolution) / 2 / allle;
                if (counte > 1) {
                    if (counte > 150) {
                        counte = 150;
                    }
                    var dettt = coordsplitbycount(geometry, parseInt(counte), 0);
                    flatCoordinates = dettt[0];
                    ends = dettt[1];
                } else if (counte < 0.25) {
                    var flast = geometry.getFlatCoordinates();
                    flatCoordinates = flast;
                    ends = [flast.length];
                    if (textState.overflow == false) {
                        ends = [];
                    }
                } else {
                    var flast = geometry.getFlatCoordinates();
                    flatCoordinates = flast;
                    ends = [flast.length];
                }
            } else {
                var featlength = geometry.getLength();
                var counte = (featlength / resolution) / 2 / allle;
                if (counte > 0.25) {
                    ends = [flatCoordinates.length];
                } else {
                    ends = [flatCoordinates.length];
                    //turn off overText show
                    if (textState.overflow == false) {
                        ends = [];
                    }
                }
            }

        } else if (geometryType == 'MultiLineString') {
            ends = (geometry).getEnds();

            if (textState.textrepeat_ == "true" || textState.textrepeat_ == true) {
                var eret = [];
                var tre = [];
                var dertt = 0;
                var mylinestrings = geometry.getLineStrings();
                for (var iz = 0; iz < mylinestrings.length; iz++) {
                    var featlength = mylinestrings[iz].getLength();
                    var counte = (featlength / resolution) / 2 / allle;
                    if (counte > 1) {
                        if (counte > 150) {
                            counte = 150;
                        }
                        var dettt = coordsplitbycount(mylinestrings[iz], parseInt(counte), dertt);
                        eret = eret.concat(dettt[0]);
                        tre = tre.concat(dettt[1]);
                        dertt = dettt[2];
                    } else if (counte < 0.15) {
                        // tre = [];
                    } else {
                        var flast = mylinestrings[iz].getFlatCoordinates();
                        eret = eret.concat(flast);
                        dertt += flast.length;
                        tre = tre.concat(dertt);
                    }
                }
                flatCoordinates = eret;
                ends = tre;
            }

        } else if (geometryType == 'Polygon') {
            ends = (geometry)
                .getEnds()
                .slice(0, 1);
        } else if (geometryType == 'MultiPolygon') {
            const endss = (geometry).getEndss();
            ends = [];
            for (let i = 0, ii = endss.length; i < ii; ++i) {
                ends.push(endss[i][0]);
            }
        }
        this.beginGeometry(geometry, feature, index);
        let repeat = textState.repeat;
        repeat = false;
        const textAlign = repeat
            ? undefined
            : textState.textAlign;
        // No `justify` support for line placement.
        let flatOffset = 0;

        var labelcoordinates = [],
            labelrotates = [];

        for (let o = 0, oo = ends.length; o < oo; ++o) {
            let chunks;
            if (repeat) {
                chunks = lineChunk(
                    repeat * this.resolution,
                    flatCoordinates,
                    flatOffset,
                    ends[o],
                    stride,
                );
            } else {
                chunks = [flatCoordinates.slice(flatOffset, ends[o])];
            }
            for (let c = 0, cc = chunks.length; c < cc; ++c) {
                const chunk = chunks[c];
                let chunkBegin = 0;
                let chunkEnd = chunk.length;
                if (textAlign == undefined) {
                    const range = matchingChunk(textState.maxAngle, chunk, 0, chunk.length, 2,);
                    chunkBegin = range[0];
                    chunkEnd = range[1];
                }

                const tempCoords = [];

                for (let i = chunkBegin; i < chunkEnd; i += stride) {
                    coordinates.push(chunk[i], chunk[i + 1]);
                    tempCoords.push([
                        chunk[i],
                        chunk[i + 1]
                    ]);
                }

                if (tempCoords.length >= 2) {
                    const line = new LineString(tempCoords);

                    let anchor;
                    switch (textState.textAlign) {
                        case 'left':
                            anchor = line.getCoordinateAt(0);
                            break;
                        case 'right':
                            anchor = line.getCoordinateAt(1);
                            break;
                        case 'center':
                        default:
                            anchor = line.getCoordinateAt(0.5);
                            break;
                    }

                    // labelcoordinates.push(anchor);
                    labelcoordinates.push(anchor[0], anchor[1]);
                    labelrotates.push(this.textRotation_);
                }

                const end = coordinates.length;
                flatOffset = ends[o];
                this.drawChars_(begin, end);
                begin = end;
            }
        }

        feature.set('current_labelCoordinates', {
            'coordinates': labelcoordinates,
            'rotates': labelrotates
        }, true);

        this.endGeometry(feature);
    } else {
        let geometryWidths = textState.overflow
            ? null
            : [];

        if (feature.get('labelCoordinates')) {
            if (typeof feature.get('labelCoordinates') == 'string') {
                var new_obj = JSON.parse(feature.get('labelCoordinates'));
                feature.set('labelCoordinates', new_obj, true);
            }

            flatCoordinates = feature.get('labelCoordinates')['coordinates']
                ? feature.get('labelCoordinates')['coordinates']
                : [];

            rotate_for_coords = feature.get('labelCoordinates')['rotates'];
            var coordinates_crs = feature.get('labelCoordinates')['proj']
                ? feature.get('labelCoordinates')['proj']
                : 'EPSG:3857';
            var h = flatCoordinates.length;
            var c = h.length == 3
                ? 3
                : 2;
            if (window.mapProjection) {
                if (window.mapProjection != coordinates_crs) {
                    for (var i1 = 0; i1 < h / 2; i1++) {
                        var transformed_coord = transform([
                            flatCoordinates[i1 * 2],
                            flatCoordinates[i1 * 2 + 1]
                        ], coordinates_crs, window.mapProjection);
                        flatCoordinates[i1 * 2] = transformed_coord[0];
                        flatCoordinates[i1 * 2 + 1] = transformed_coord[1];
                    }
                    var e_obj = feature.get('labelCoordinates');
                    e_obj['proj'] = window.mapProjection;
                    feature.set('labelCoordinates', e_obj, true);
                }
            }
            feature.set('current_labelCoordinates', {
                'coordinates': flatCoordinates,
                'rotates': rotate_for_coords
            }, true);

        } else {

            let p;

            switch (geometryType) {
                case 'Point':
                case 'MultiPoint':
                    flatCoordinates = (geometry).getFlatCoordinates();
                    break;
                case 'LineString':
                    flatCoordinates = (geometry).getFlatMidpoint();

                    if (textState.textrepeat_ == "true" || textState.textrepeat_ == true) {
                        var featlength = geometry.getLength();
                        var counte = (featlength / resolution) / 2 / allle;
                        if (counte > 1) {
                            if (counte > 150) {
                                counte = 150;
                            }
                            var dettt = coordsplitbycount(geometry, parseInt(counte), 0);
                            flatCoordinates = dettt[0];
                            p = dettt[1];
                        } else if (counte < 0.25) {
                            var flast = geometry.getFlatCoordinates();
                            flatCoordinates = flast;
                            p = [flast.length];
                            if (textState.overflow == false) {
                                p = [];
                            }
                        } else {
                            var flast = geometry.getFlatCoordinates();
                            flatCoordinates = flast;
                            p = [flast.length];
                        }
                    } else {
                        var featlength = geometry.getLength();
                        var counte = (featlength / resolution) / 2 / allle;
                        if (counte > 0.25) {
                            p = [flatCoordinates.length];
                        } else {
                            p = [flatCoordinates.length];
                            //turn off overText show
                            if (textState.overflow == false) {
                                p = [];
                            }
                        }
                    }

                    var labelcoordinates = [];
                    for (
                        var d = textState.textAlign,
                        g = 0,
                        y = void 0,
                        m = 0,
                        v = p.length;
                        m < v;
                        ++m
                    ) {
                        if (null == d) {
                            var _ = matchingChunk(
                                textState.maxAngle,
                                flatCoordinates,
                                g,
                                p[m],
                                geometryWidths
                            );
                            g = _[0],
                            y = _[1]
                        } else 
                            y = p[m];
                        
                        var temp_coords = [];

                        for (var o = g; o < y; o += c) {
                            temp_coords.push([
                                flatCoordinates[o],
                                flatCoordinates[o + 1]
                            ]);

                        }
                        var h = this.coordinates.length,
                            g = p[m];

                        var this_label_coord = (new LineString(temp_coords)).getCoordinateAt(0.5);
                        labelcoordinates = labelcoordinates.concat(this_label_coord);
                    }
                    flatCoordinates = labelcoordinates;
                    var c = 2;
                    var h = flatCoordinates.length;

                    break;
                case 'Circle':
                    flatCoordinates = (geometry).getCenter();
                    break;
                case 'MultiLineString':
                    flatCoordinates = (geometry).getFlatMidpoints();
                    stride = 2;
                    break;
                case 'Polygon':
                    flatCoordinates = (geometry).getFlatInteriorPoint();
                    if (!textState.overflow) {
                        geometryWidths.push(flatCoordinates[2] / this.resolution);
                    }
                    stride = 3;

                    if (textState.textrepeat_ == "true" || textState.textrepeat_ == true) {
                        var mycoords = polygonsplit(geometry);
                        flatCoordinates = mycoords;
                        stride = flatCoordinates.length == 3
                            ? 3
                            : 2;
                    } else {
                        flatCoordinates.length = 2;
                        stride = 2;
                    }

                    break;
                case 'MultiPolygon':
                    const interiorPoints = (geometry).getFlatInteriorPoints();
                    flatCoordinates = [];
                    if (textState.textrepeat_ == "true" || textState.textrepeat_ == true) {
                        var mypolygons = geometry.getPolygons();
                        var allcoords = [];
                        for (let i = 0, ii = mypolygons.length; i < ii; i++) {
                            var mycoords = polygonsplit(mypolygons[i]);
                            allcoords = allcoords.concat(mycoords);
                        }
                        flatCoordinates = allcoords;
                    } else {
                        for (let i = 0, ii = interiorPoints.length; i < ii; i += 3) {
                            if (!textState.overflow) {
                                geometryWidths.push(interiorPoints[i + 2] / this.resolution);
                            }
                            flatCoordinates.push(interiorPoints[i], interiorPoints[i + 1]);
                        }
                    }
                    if (flatCoordinates.length === 0) {
                        return;
                    }
                    stride = 2;
                    break;
                default:
            }

            rotate_for_coords = [];
            for (var i2 = 0; i2 < flatCoordinates.length / 2; i2++) {
                rotate_for_coords.push(this.textRotation_);
            }

            //here
            feature.set('current_labelCoordinates', {
                'coordinates': flatCoordinates,
                'rotates': rotate_for_coords
            }, true);

        }

        const end = this.appendFlatPointCoordinates(flatCoordinates, stride);
        if (end === begin) {
            return;
        }
        if (geometryWidths && (end - begin) / 2 !== flatCoordinates.length / stride) {
            let beg = begin / 2;
            geometryWidths = geometryWidths.filter((w, i) => {
                const keep = coordinates[(beg + i) * 2] === flatCoordinates[i * stride] && coordinates[(beg + i) * 2 + 1] === flatCoordinates[i * stride + 1];
                if (!keep) {
                    --beg;
                }
                return keep;
            });
        }

        this.saveTextStates_();

        const backgroundFill = textState.backgroundFill
            ? this.createFill(this.fillStyleToState(textState.backgroundFill))
            : null;
        const backgroundStroke = textState.backgroundStroke
            ? this.createStroke(this.strokeStyleToState(textState.backgroundStroke))
            : null;

        this.beginGeometry(geometry, feature, index);

        // adjust padding for negative scale
        let padding = textState.padding;
        if (padding != defaultPadding && (textState.scale[0] < 0 || textState.scale[1] < 0)) {
            let p0 = textState.padding[0];
            let p1 = textState.padding[1];
            let p2 = textState.padding[2];
            let p3 = textState.padding[3];
            if (textState.scale[0] < 0) {
                p1 = -p1;
                p3 = -p3;
            }
            if (textState.scale[1] < 0) {
                p0 = -p0;
                p2 = -p2;
            }
            padding = [p0, p1, p2, p3];
        }

        // The image is unknown at this stage so we pass null; it will be computed at
        // render time. For clarity, we pass NaN for offsetX, offsetY, width and height,
        // which will be computed at render time.
        const pixelRatio = this.pixelRatio;

        for (var i2 = begin, i3 = 0; i2 < end; i2 += 2, i3++) {

            var begin1 = i2;
            var end1 = i2 + 2;
            var new_rotate = this.textRotation_;
            new_rotate = rotate_for_coords[i3] || rotate_for_coords[i3] == 0
                ? rotate_for_coords[i3]
                : new_rotate;

            this
                .instructions
                .push([
                    CanvasInstruction.DRAW_IMAGE,
                    begin1,
                    end1,
                    null,
                    NaN,
                    NaN,
                    NaN,
                    1,
                    0,
                    0,
                    this.textRotateWithView_,
                    new_rotate,
                    [
                        1, 1
                    ],
                    NaN,
                    this.declutterMode_,
                    this.declutterImageWithText_,
                    padding == defaultPadding
                        ? defaultPadding
                        : padding.map(function (p) {
                            return p * pixelRatio;
                        }),
                    backgroundFill,
                    backgroundStroke,
                    this.text_,
                    this.textKey_,
                    this.strokeKey_,
                    this.fillKey_,
                    this.textOffsetX_,
                    this.textOffsetY_,
                    geometryWidths
                ]);
            const scale = 1 / pixelRatio;
            // Set default fill for hit detection background
            const hitDetectionBackgroundFill = backgroundFill
                ? backgroundFill.slice(0)
                : null;
            if (hitDetectionBackgroundFill) {
                hitDetectionBackgroundFill[1] = defaultFillStyle;
            }
            this
                .hitDetectionInstructions
                .push([
                    CanvasInstruction.DRAW_IMAGE,
                    begin1,
                    end1,
                    null,
                    NaN,
                    NaN,
                    NaN,
                    1,
                    0,
                    0,
                    this.textRotateWithView_,
                    new_rotate,
                    [
                        scale, scale
                    ],
                    NaN,
                    this.declutterMode_,
                    this.declutterImageWithText_,
                    padding,
                    hitDetectionBackgroundFill,
                    backgroundStroke,
                    this.text_,
                    this.textKey_,
                    this.strokeKey_,
                    this.fillKey_
                        ? defaultFillStyle
                        : this.fillKey_,
                    this.textOffsetX_,
                    this.textOffsetY_,
                    geometryWidths
                ]);

        }

        this.endGeometry(feature);
    }
}

export default TextBuilder;

// Re-export ALL named exports from OL export * from
// 'ol/render/canvas/TextBuilder.js';